import {data, isEditMode, layers, referenceTimestamp, timeline} from "./common";
import {fetchJson} from "./utils";
import {applyLiveWindMeasures, LIVE_WIND_URL, refreshTempSensorsData, refreshWaveSensorsData} from "./data";
import {alignToLiveHead, ensureTimelineLayout, initTimelineFrame, renderTimeline} from "./timeline";
import {planSync, retryDelay, SYNC} from "./sync-plan";

// Keeps the observations displayed on the map and the timeline up to date,
// whatever happened to the page meanwhile (hidden tab, frozen or discarded
// page, sleeping device, network down, installed PWA sent to background).
//
// Every trigger (heartbeat, visibility, focus, bfcache restore, network back,
// manual refresh) funnels into requestSync(), which decides from the time
// elapsed since the last successful loads what has to be reloaded (see
// sync-plan.js). Only one sync runs at a time, failures are retried with a
// backoff.

const HISTORY_URL = "https://private2.windmorbihan.com/mesures/history.json";
const HISTORY_TIMEOUT_MS = 45000;
const LIVE_TIMEOUT_MS = 15000;
const LOG_SIZE = 50;

const state = {
  frameDay: null,
  lastHistorySync: 0,
  lastLiveSync: 0,
  historyNeeded: false,
  nextAttemptAt: 0,
  failures: 0,
  running: null,
  pending: null,
  triggersInstalled: false,
  log: []
};

export function startSync(refTs) {
  return Promise.resolve().then(() => {
    initTimelineFrame(refTs);
    state.frameDay = new Date().toDateString();
    installTriggers();
    return requestSync('init', {forceFull: true, forceAlign: true});
  });
}

export function refreshNow() {
  alignToLiveHead(true);
  timeline.followLive = true;
  return requestSync('manual', {forceFull: true, forceAlign: true});
}

export function requestSync(reason, {forceFull = false, forceAlign = false} = {}) {
  if (!timeline.timelineSteps) {
    return Promise.resolve();
  }
  if (state.running) {
    const pending = state.pending || {forceFull: false, forceAlign: false};
    state.pending = {
      reason,
      forceFull: pending.forceFull || forceFull,
      forceAlign: pending.forceAlign || forceAlign
    };
    return state.running;
  }

  const now = Date.now(), today = new Date().toDateString();
  const plan = planSync(state, {
    now,
    today,
    visible: document.visibilityState !== 'hidden',
    editMode: isEditMode(),
    followLive: timeline.followLive,
    periodic: reason === 'tick',
    forceFull,
    forceAlign
  });
  if (!plan.kind) {
    return Promise.resolve();
  }

  if (plan.rebuildFrame) {
    initTimelineFrame(referenceTimestamp());
    state.frameDay = today;
  }
  timeline.followLive = plan.followLive;

  state.running = (plan.kind === 'full' ? fullSync(plan.align) : liveSync(plan.align))
    .then(() => {
      state.failures = 0;
      state.nextAttemptAt = 0;
      record(reason, plan, now, 'ok');
    }, (err) => {
      state.nextAttemptAt = Date.now() + retryDelay(state.failures);
      state.failures++;
      record(reason, plan, now, 'error: ' + (err && err.message || err));
    })
    .finally(() => {
      state.running = null;
      const pending = state.pending;
      state.pending = null;
      if (pending) {
        requestSync(pending.reason, pending);
      }
    });
  return state.running;
}

function fullSync(align) {
  state.historyNeeded = true;
  return Promise.allSettled([
    fetchJson(HISTORY_URL, {timeoutMs: HISTORY_TIMEOUT_MS}),
    fetchJson(LIVE_WIND_URL, {timeoutMs: LIVE_TIMEOUT_MS})
  ]).then(([history, live]) => {
    const now = Date.now();
    if (history.status === 'fulfilled') {
      data.timelineData = history.value;
      state.lastHistorySync = now;
      state.historyNeeded = false;
    }
    if (live.status === 'fulfilled') {
      state.lastLiveSync = now;
    }
    if (history.status === 'fulfilled' || live.status === 'fulfilled') {
      applyMeasures(live.status === 'fulfilled' ? live.value : null, align);
    }
    const failure = [history, live].find(result => result.status === 'rejected');
    if (failure) {
      throw failure.reason;
    }
  });
}

function liveSync(align) {
  return fetchJson(LIVE_WIND_URL, {timeoutMs: LIVE_TIMEOUT_MS}).then((live) => {
    state.lastLiveSync = Date.now();
    applyMeasures(live, align);
  });
}

function applyMeasures(live, align) {
  const following = timeline.followLive && !isEditMode();
  if (live) {
    applyLiveWindMeasures(live, following);
  } else {
    renderTimeline(true);
  }
  if (following) {
    alignToLiveHead(align);
    if (layers.dataLayers.indexOf('waves') !== -1) {
      refreshWaveSensorsData();
    }
    if (layers.dataLayers.indexOf('temp') !== -1) {
      refreshTempSensorsData();
    }
  }
}

function installTriggers() {
  if (state.triggersInstalled) {
    return;
  }
  state.triggersInstalled = true;
  setInterval(() => {
    ensureTimelineLayout();
    requestSync('tick');
  }, SYNC.TICK_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      ensureTimelineLayout();
      requestSync('visible');
    }
  });
  window.addEventListener('pageshow', (evt) => {
    if (evt.persisted) {
      requestSync('pageshow');
    }
  });
  document.addEventListener('resume', () => requestSync('resume'));
  window.addEventListener('focus', () => requestSync('focus'));
  window.addEventListener('online', () => requestSync('online'));
}

function record(reason, plan, startedAt, result) {
  const entry = {
    at: new Date(startedAt).toLocaleTimeString(),
    reason,
    kind: plan.kind,
    align: plan.align,
    rebuildFrame: plan.rebuildFrame,
    ms: Date.now() - startedAt,
    result
  };
  state.log.push(entry);
  if (state.log.length > LOG_SIZE) {
    state.log.shift();
  }
  (result === 'ok' ? console.debug : console.warn)('[sync] ' + JSON.stringify(entry));
}

export function syncStatus() {
  const time = (ms) => ms ? new Date(ms).toLocaleString() : null;
  return {
    followLive: timeline.followLive,
    frameDay: state.frameDay,
    lastHistorySync: time(state.lastHistorySync),
    lastLiveSync: time(state.lastLiveSync),
    historyNeeded: state.historyNeeded,
    failures: state.failures,
    nextAttemptAt: time(state.nextAttemptAt),
    running: Boolean(state.running),
    log: state.log.slice()
  };
}
